import { Button, Icon } from '../ui';
import React, { useState, useRef } from 'react';
import { useAttachmentDraft } from '../../hooks/useAttachmentDraft';
import { AttachmentPicker } from '../SessionAttachments';

/**
 * The one input surface for a session.
 *
 * It switches mode rather than existing or not existing. The old page gated its
 * input on `session.status === 'finished'` and rendered the answer field only
 * while a request was pending, which left a window right after answering where
 * the user had just typed something and no input box was on screen at all.
 *
 * A pending question takes the composer over: the transcript keeps only an
 * outcome row, so a question is never rendered twice. It also lives outside the
 * view tabs, so a blocked agent can still be answered from another tab.
 */
export default function Composer({
  mode,                 // 'answer' | 'message' | 'disabled'
  question,             // the pending HumanRequest, when mode === 'answer'
  extraPendingCount,    // how many further questions are queued behind it
  onSubmit,             // (text) => Promise
  sending,
  error,
  projectId,
}) {
  const attachments = useAttachmentDraft(projectId);
  const [draft, setDraft] = useState('');
  const draftVersion = useRef(0);
  const questionId = question?.human_request_id;
  React.useEffect(() => { setDraft(''); draftVersion.current += 1; }, [questionId]);

  if (mode === 'disabled') return null;

  const answering = mode === 'answer';

  const submit = async (text) => {
    const value = (text ?? draft).trim();
    if ((!value && !attachments.ids.length) || !attachments.ready || sending) return;
    // The draft is cleared only once onSubmit resolves — a failed or reverted
    // send must never destroy what the user typed.
    const sentVersion = draftVersion.current;
    const sentIds = [...attachments.ids];
    const confirmed = () => {
      attachments.clear(sentIds);
      if (sentVersion === draftVersion.current) setDraft(current => current.trim() === value ? '' : current);
    };
    try { await onSubmit(value, sentIds, confirmed); } catch { return; }
  };

  return (
    <div className={'chat-composer' + (answering ? ' chat-composer-answer' : '')}>
      {answering && question && (
        <div className="composer-question">
          <div className="bubble-label bubble-label-urgent">Требуется ваше решение</div>
          <div className="composer-question-text">{question.text}</div>
          {question.context_summary && (
            <div className="text-muted" style={{ fontSize: '0.82rem', marginTop: 4 }}>
              {question.context_summary}
            </div>
          )}
          {question.options?.length > 0 && (
            <div className="flex gap-2 mt-2" style={{ flexWrap: 'wrap' }}>
              {question.options.map((opt, i) => (
                <Button
                  key={i}
                  className="btn btn-secondary btn-sm"
                  disabled={sending}
                  onClick={() => submit(opt.label || opt.value || String(i))}
                >
                  <span>{opt.label || opt.value || `Вариант ${i + 1}`}{opt.recommended ? ' · рекомендуется' : ''}</span>
                  {opt.description && <small style={{display:'block', fontWeight:400}}>{opt.description}</small>}
                </Button>
              ))}
            </div>
          )}
          {extraPendingCount > 0 && (
            <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: 6 }}>
              Ещё вопросов в очереди: {extraPendingCount}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="composer-error">
          Не удалось отправить: {error?.response?.data?.error?.message || error?.message || 'ошибка сети'}
        </div>
      )}

      <AttachmentPicker draft={attachments} disabled={sending} compact>
        <textarea
          className="form-control"
          aria-label={answering ? 'Ответ на вопрос' : 'Сообщение агентам'}
          rows={2}
          style={{ flex: 1 }}
          value={draft}
          onChange={e => { draftVersion.current += 1; setDraft(e.target.value); }}
          placeholder={answering ? 'Свой вариант — напишите ваш ответ...' : 'Написать сообщение агентам...'}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <Button
          className="btn btn-primary"
          disabled={sending || !attachments.ready || (!draft.trim() && !attachments.ids.length)}
          onClick={() => submit()}
        >
          <Icon name="send" size={18} />
          {sending ? '...' : answering ? 'Ответить' : 'Отправить'}
        </Button>
      </AttachmentPicker>
      <p className="composer-hint">Enter — отправить · Shift+Enter — новая строка</p>
    </div>
  );
}
